import { Component, input, OnDestroy, output, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DataTable, IDataClassConfig } from 'app/classes/data-table/data-table';
import { Issue } from 'app/classes/issue';
import { Org } from 'app/classes/org';
import { IDataTableActionEmit } from 'app/layout/data-table/data-table/data-table.component';
import { APIService } from 'app/shared/api.service';
import { DateService } from 'app/shared/date.service';
import { ExportService } from 'app/shared/export.service';
import { IssueTrackingService, ITrackingIssue } from 'app/shared/issue-tracking.service';
import { WindowService } from 'app/shared/window.service';

@Component({
  selector: 'app-issue-tracking-list',
  standalone: false,
  templateUrl: './issue-tracking-list.component.html',
  styleUrl: './issue-tracking-list.component.css'
})
export class IssueTrackingListComponent implements OnDestroy {
  isPicker = input<boolean>(false);
  hasLoaded = signal<boolean>(false);
  isEnabled = signal<boolean>(false);
  issues = signal<ITrackingIssue[]>(null);
  filteredIssues = signal<ITrackingIssue[]>(null);
  dataTable = signal<DataTable>(null);
  dataClassConfig: IDataClassConfig;
  isStaff = signal<boolean>(false);
  toggles: { state: 'Open' | 'Closed' | 'All', visibility: 'All' | 'Public' | 'Private' } = { state: 'Open', visibility: 'All' };
  showComments = signal<boolean>(false);
  searchInputText = signal<string>(null);
  searchInputCount = signal<number>(null);
  thisOrg: Org;
  isMobile: boolean;
  onIssueSelected = output<ITrackingIssue>();
  dialog = signal<'update' | 'export'>(null);
  issueId = signal<number>(null);

  constructor(private apiService: APIService, private issueTracking: IssueTrackingService, private router: Router, private activatedRoute: ActivatedRoute, private windowService: WindowService, private exportService: ExportService) {
    this.isStaff.set(apiService.isAdmin());
    this.getLocalStorage();
    this.get();
    this.thisOrg = apiService.getUserOrg();
    this.isMobile = this.windowService.isMobile();
  }

  setLocalStorage() {
    localStorage.setItem('issuetracking:list', JSON.stringify({ toggles: this.toggles, searchInputText: this.searchInputText() }));
  }

  getLocalStorage() {
    try {
      const objString = localStorage.getItem('issuetracking:list');
      if (objString) {
        const obj = JSON.parse(objString);
        this.toggles = { ...this.toggles, ...obj.toggles };
        this.searchInputText.set(obj.searchInputText ?? null);
      }
    } catch (e) {

    }
  }

  toggleCommentState() {
    this.showComments.set(!this.showComments());
    this.filter();
  }

  toggleState() {
    this.toggles.state = this.toggles.state === 'Open' ? 'Closed' : this.toggles.state === 'Closed' ? 'All' : 'Open';
    this.get();
  }

  toggleVisibility() {
    this.toggles.visibility = this.toggles.visibility === 'All' ? 'Public' : this.toggles.visibility === 'Public' ? 'Private' : 'All';
    this.get();
  }

  get() {
    this.issueTracking.getIssues(this.toggles.state, this.toggles.visibility).then(response => {
      console.log('RESPONSE', response);
      const { isEnabled } = response.master;
      this.isEnabled.set(isEnabled);

      if (isEnabled) {
        this.issues.set(response.issues);
        this.filter();
      }

      this.hasLoaded.set(true);
    });
  }

  actionClick(response: IDataTableActionEmit) {
    if (response?.row?.id) {
      this.issueId.set(response.row.id);
      this.dialog.set('update');
    }
  }

  filter() {
    console.log(`filter`, this.searchInputText());

    if (this.isPicker()) {
      this.dataClassConfig = {
        "rag": { valueType: "rag", title: 'RAG', align: 'left' },
        "siteTitle": { valueType: "string", title: 'Site', align: 'left' },
        "title": { valueType: "string", title: 'Summary', align: 'left' },
        "subStatusTitle": { valueType: "string", title: 'Status', align: 'left' },
      };
    } else {
      if (this.thisOrg.id === 4) {
        if (this.showComments()) {
          this.dataClassConfig = {
            "rag": { valueType: "rag", title: 'RAG', align: 'left' },
            "org": { valueType: "string", title: 'Org', align: 'left' },
            "createdAt": { valueType: "date", title: 'Created', align: 'left', width: 110, dataFormat: 'date' },
            "title": { valueType: "string", title: 'Summary', align: 'left', width: 400 },
            "lastComment": { valueType: "string", title: 'Comment', align: 'left' },
            "lastCommentType": { valueType: "string", title: 'Type', align: 'left', width: 100 },
            "lastCommentDate": { valueType: "date", dataFormat: "date", title: "Date", align: 'right', width: 100 },
          };
        } else {
          this.dataClassConfig = {
            "rag": { valueType: "rag", title: 'RAG', align: 'left' },
            "org": { valueType: "string", title: 'Org', align: 'left' },
            "createdAt": { valueType: "date", title: 'Created', align: 'left', width: 110, dataFormat: 'date' },
            "title": { valueType: "string", title: 'Summary', align: 'left' },
            "status": { valueType: "string", title: 'Status', align: 'left' },
            "subStatusTitle": { valueType: "string", title: 'Sub Status', align: 'left' },
            "assetCount": { valueType: "number", title: 'Asset', align: 'left', tooltip: 'Number of assets' },
            "completedAt": { valueType: "date", dataFormat: "date", title: "Completed", align: 'right', width: 100 },
            "resolvedAt": { valueType: "date", dataFormat: "date", title: "Resolved", align: 'right', width: 100 },
            "updatedAtDays": { valueType: "number", title: 'Days', align: 'right', tooltip: 'Days since last update' },
          };
        }
      } else {
        if (this.isMobile) {
          this.dataClassConfig = {
            "rag": { valueType: "rag", title: 'RAG', align: 'left' },
            "siteTitle": { valueType: "string", title: 'Site', align: 'left' },
            "title": { valueType: "string", title: 'Summary', align: 'left' },
            "subStatusTitle": { valueType: "string", title: 'Status', align: 'left' },
          };
        } else {
          if (this.showComments()) {
            this.dataClassConfig = {
              "rag": { valueType: "rag", title: 'RAG', align: 'left' },
              "org": { valueType: "string", title: 'Org', align: 'left' },
              "createdAt": { valueType: "date", title: 'Created', align: 'left', width: 110, dataFormat: 'date' },
              "title": { valueType: "string", title: 'Summary', align: 'left', width: 400 },
              "lastComment": { valueType: "string", title: 'Comment', align: 'left' },
              "lastCommentType": { valueType: "string", title: 'Type', align: 'left', width: 100 },
              "lastCommentDate": { valueType: "date", dataFormat: "date", title: "Date", align: 'right', width: 100 },
            };
          } else {
            this.dataClassConfig = {
              "rag": { valueType: "rag", title: 'RAG', align: 'left' },
              "siteTitle": { valueType: "string", title: 'Site', align: 'left' },
              "createdAt": { valueType: "date", title: 'Created', align: 'left', width: 110, dataFormat: 'date' },
              "title": { valueType: "string", title: 'Summary', align: 'left' },
              "status": { valueType: "string", title: 'Status', align: 'left' },
              "subStatusTitle": { valueType: "string", title: 'Sub Status', align: 'left' },
              "completedAt": { valueType: "date", dataFormat: "date", title: "Completed", align: 'right', width: 100 },
              "resolvedAt": { valueType: "date", dataFormat: "date", title: "Resolved", align: 'right', width: 90 },
              "assetCount": { valueType: "number", title: 'Assets', align: 'right', width: 50 },
              "updatedAtDays": { valueType: "number", title: 'Age', align: 'right', tooltip: 'Days since last update', width: 60 },
            };
          }
        }
      }
    }

    if (this.issues().length) {
      let filteredIssues;
      if (this.searchInputText()?.length) {
        let searchText = this.searchInputText()?.toLowerCase();
        filteredIssues = this.issues().filter(i => {
          let match = false;

          if (['red', 'amber', 'orange', 'green'].includes(searchText)) {
            if (searchText === 'orange') searchText = 'amber';
            // must match rag
            return i.rag === searchText;
          }
          if (searchText.includes('id:')) {
            if (i.id == Number(searchText.replace('id:', ''))) {
              match = true;
            }
          }

          if (i.siteTitle.toLowerCase().includes(searchText)) match = true;
          if (i.description.toLowerCase().includes(searchText)) match = true;
          if (i.title.toLowerCase().includes(searchText)) match = true;
          if (i.createdByName.toLowerCase().includes(searchText)) match = true;
          if (i.status.toLowerCase().includes(searchText)) match = true;
          if (i.resolution?.toLowerCase().includes(searchText)) match = true;
          if (i.subStatusTitle?.toLowerCase().includes(searchText)) match = true;
          if (i.assetTitles?.[0]?.toLowerCase().includes(searchText)) match = true;
          return match;
        });
      } else {
        filteredIssues = this.issues();
      }
      this.searchInputCount.set(filteredIssues.length);

      const filteredData = filteredIssues.map(i => {
        const org = i.orgShortTitles?.[0] ?? i.orgShortTitle ?? undefined;

        return { ...i, org, updatedAtDays: i.update_info.updatedAtDays };
      });

      this.dataTable.set(new DataTable({
        data: filteredData, sortedColumns: null, rowKey: 'id'
      }, this.dataClassConfig));
    } else {
      this.dataTable.set(null);
    }
  }

  rowClick(row: ITrackingIssue) {
    if (this.isPicker()) {
      this.onIssueSelected.emit(row);
    } else {
      this.router.navigate(['issue', row.id], { relativeTo: this.activatedRoute });
    }
  }

  addIssue() {
    this.router.navigate(['create'], { relativeTo: this.activatedRoute });
  }

  searchChanged(text: string) {
    this.searchInputText.set(text);
    this.filter();
  }

  issueUpdated(issue: Issue) {
    this.dialog.set(null);
    this.get();
  }

  issueCompleted(ev: any) {
    this.dialog.set(null);
    this.get();
  }

  exportClick() {
    // this.dialog.set('export');
    this.startExport();
  }

  startExport(data?: any) {
    console.log('startExport', data);
    const now = DateService.date(new Date, '-');
    const filename = `issue_tracking_${now}` + (this.searchInputText()?.length ? '_' + this.searchInputText() : '');

    this.exportService.exportIssueTrackingList(this.dataTable().data, filename);
  }

  ngOnDestroy(): void {
    this.setLocalStorage();
  }
}
